import type {
  AppointmentListItem,
  AppointmentStatus,
} from "../../../shared/api-types.js";
import { isWellFormedRdSelection } from "../../../shared/selected-rd.js";
import {
  isFutureWeek,
  isIsoMondayDate,
  type PastWeekClock,
} from "./appointment-past-week.js";

export interface AppointmentDateGroup {
  date: string;
  label: string;
  appointments: AppointmentListItem[];
}

export interface AppointmentListSearch {
  batches?: number;
  showNonDefaultStatuses?: true;
  /**
   * The RD an admin selected (NO-467); never restored or defaulted. In search
   * state so it survives navigation and the siderail can read it without a prop.
   */
  rdId?: string;
  /**
   * NO-767: the Past view; absent is Upcoming. NO-874: "week" is the Week
   * view, shown only behind rd-schedule in the single-rail frame; a view the
   * frame cannot show falls back to Upcoming. NO-878: "availability" is the
   * hours editor, behind its own gate.
   */
  view?: "past" | "week" | "availability";
  /**
   * NO-767: the Past week's ISO Monday; absent opens the newest week. In the
   * Week view (NO-874) any ISO Monday, absent meaning the current week.
   */
  week?: string;
  /** NO-878: Availability's One week editor; absent is Every week. */
  mode?: "week";
  /** NO-878: One week's ISO Monday; absent opens the RD's next week. */
  availWeek?: string;
}

/** The page's views, in tab order. */
export type AppointmentView = "upcoming" | "week" | "past" | "availability";

export interface AppointmentViewTab {
  value: AppointmentView;
  label: string;
}

/**
 * The tab row, built from one ordered list: Upcoming, Week, Past, Availability.
 * Week is listed while NO-874's gate is open and Availability while its gate
 * (`availabilityGate` in `rd-hours-client.ts`) is open.
 */
export function appointmentViewTabs(
  availability: boolean,
  week: boolean
): AppointmentViewTab[] {
  return [
    { value: "upcoming", label: "Upcoming" },
    ...(week ? [{ value: "week" as const, label: "Week" }] : []),
    { value: "past", label: "Past" },
    ...(availability
      ? [{ value: "availability" as const, label: "Availability" }]
      : []),
  ];
}

export const MAX_RESTORED_APPOINTMENT_BATCHES = 10;

function deviceClock(): PastWeekClock {
  return { now: new Date().toISOString(), timezone: deviceTimezone() };
}

/**
 * Shared by the list and the detail route, so every key kept here survives
 * onto an opened Appointment and back. Values the server would refuse are
 * dropped rather than carried: a week needs view=past and must be a real ISO
 * Monday no later than the device's current week, and Past has no batches.
 */
export function validateAppointmentListSearch(
  search: Record<string, unknown>,
  clock: () => PastWeekClock = deviceClock
): AppointmentListSearch {
  const showNonDefaultStatuses =
    search.showNonDefaultStatuses === true || search.showNonDefaultStatuses === "true";
  const rdId = isWellFormedRdSelection(search.rdId) ? search.rdId : undefined;
  const view = readView(search.view);
  const batches = view ? undefined : readBatchCount(search.batches);
  const week = readViewWeek(view, search.week, clock);

  return {
    ...(batches ? { batches } : {}),
    ...(view === "availability" ? readAvailabilitySearch(search) : {}),
    ...(view && view !== "availability" ? { view } : {}),
    ...(week ? { week } : {}),
    ...(showNonDefaultStatuses ? { showNonDefaultStatuses: true as const } : {}),
    ...(rdId ? { rdId } : {}),
  };
}

/**
 * Availability keeps its editor mode and, in One week, a real ISO Monday. An
 * ended week is the server's call (400 `week_ended`): the client cannot know
 * the RD's zone before the read.
 */
function readAvailabilitySearch(
  search: Record<string, unknown>
): AppointmentListSearch {
  const isWeekMode = search.mode === "week";
  const availWeek =
    isWeekMode && isIsoMondayDate(search.availWeek) ? search.availWeek : undefined;

  return {
    view: "availability",
    ...(isWeekMode ? { mode: "week" as const } : {}),
    ...(availWeek ? { availWeek } : {}),
  };
}

const SEARCH_VIEWS: ReadonlySet<unknown> = new Set(["past", "week", "availability"]);

function readView(value: unknown): AppointmentListSearch["view"] {
  if (!SEARCH_VIEWS.has(value)) return undefined;

  return value as AppointmentListSearch["view"];
}

function readViewWeek(
  view: AppointmentListSearch["view"],
  value: unknown,
  clock: () => PastWeekClock
): string | undefined {
  if (view === "past") return readPastWeek(value, clock);

  return view === "week" && isIsoMondayDate(value) ? value : undefined;
}

function readPastWeek(value: unknown, clock: () => PastWeekClock): string | undefined {
  if (!isIsoMondayDate(value)) return undefined;

  return isFutureWeek(value, clock()) ? undefined : value;
}

export function appointmentBatchCount(search: AppointmentListSearch): number {
  return search.batches ?? 1;
}

export function nextAppointmentBatchSearch(
  search: AppointmentListSearch,
  loadedBatches: number
): AppointmentListSearch {
  const batches = Math.min(
    MAX_RESTORED_APPOINTMENT_BATCHES,
    Math.max(2, loadedBatches + 1)
  );

  return {
    ...search,
    batches,
  };
}

// Filter and paging changes are navigations, so they must carry the selection
// forward — dropping it would silently unselect the RD mid-read. The toggle
// keeps the view and the Past week too; only Upcoming's batch depth resets.
export function appointmentStatusSearch(
  search: AppointmentListSearch,
  showNonDefaultStatuses: boolean
): AppointmentListSearch {
  return {
    ...(search.view ? { view: search.view } : {}),
    ...(search.week ? { week: search.week } : {}),
    ...(search.mode ? { mode: search.mode } : {}),
    ...(search.availWeek ? { availWeek: search.availWeek } : {}),
    ...(search.rdId ? { rdId: search.rdId } : {}),
    ...(showNonDefaultStatuses ? { showNonDefaultStatuses: true as const } : {}),
  };
}

/** Switching views keeps the selection and the toggle; Past opens on its newest week. */
export function appointmentViewSearch(
  search: AppointmentListSearch,
  view: AppointmentView
): AppointmentListSearch {
  if (view === "availability") {
    // The status toggle means nothing here; the editor opens on Every week.
    return { view, ...(search.rdId ? { rdId: search.rdId } : {}) };
  }

  return {
    ...(view === "upcoming" ? {} : { view }),
    ...(search.showNonDefaultStatuses ? { showNonDefaultStatuses: true as const } : {}),
    ...(search.rdId ? { rdId: search.rdId } : {}),
  };
}

/** A Past week change: the same view, selection, and toggle on another week. */
export function appointmentWeekSearch(
  search: AppointmentListSearch,
  week: string
): AppointmentListSearch {
  return {
    view: "past",
    week,
    ...(search.showNonDefaultStatuses ? { showNonDefaultStatuses: true as const } : {}),
    ...(search.rdId ? { rdId: search.rdId } : {}),
  };
}

/**
 * NO-874: a Week view change: the same view and selection on another ISO
 * Monday; null is the current week, which the address leaves unnamed.
 */
export function appointmentWeekViewSearch(
  search: AppointmentListSearch,
  week: string | null
): AppointmentListSearch {
  return {
    view: "week",
    ...(week ? { week } : {}),
    ...(search.rdId ? { rdId: search.rdId } : {}),
  };
}

/**
 * The detail breadcrumb's way back: the view, week, toggle, and selection,
 * without Upcoming's batch depth.
 */
export function appointmentListHref(search: AppointmentListSearch): string {
  const query = new URLSearchParams();
  if (search.view) query.set("view", search.view);

  if (search.week) query.set("week", search.week);

  if (search.mode) query.set("mode", search.mode);

  if (search.availWeek) query.set("availWeek", search.availWeek);

  if (search.showNonDefaultStatuses) query.set("showNonDefaultStatuses", "true");

  if (search.rdId) query.set("rdId", search.rdId);

  const encoded = query.toString();

  return encoded ? `/appointments?${encoded}` : "/appointments";
}

/**
 * The view the page shows. Week (NO-874) needs rd-schedule in the single-rail
 * frame; a view the frame cannot show is Upcoming. Availability (NO-878) is
 * kept as addressed: the page answers it with its own gate (blank, 404, panel).
 */
export function appointmentViewFor(
  search: AppointmentListSearch,
  weekEnabled: boolean
): AppointmentView {
  if (search.view === "past" || search.view === "availability") return search.view;

  return search.view === "week" && weekEnabled ? "week" : "upcoming";
}

/**
 * Selecting an RD resets filters and paging: it is a different schedule. On
 * Availability the view stays (the editor opens on that RD's Every week).
 */
export function appointmentRdSearch(
  rdId: string | null,
  view?: AppointmentView
): AppointmentListSearch {
  const availability = view === "availability" ? { view } : {};

  return rdId ? { ...availability, rdId } : availability;
}

/**
 * Whether rows fetched under `previousKey` may stay on screen as placeholder
 * while the next list request is in flight. Only within one selection: the
 * own lane keeps its rows across a filter toggle, but a change of RD — or
 * Browser Back to no selection — must unmount the previous RD's patients
 * rather than hold them until the new answer arrives (NO-467).
 */
export function keepsPreviousAppointmentRows(
  previousKey: readonly unknown[] | undefined,
  rdId: string | undefined
): boolean {
  if (!previousKey) return false;

  return appointmentQueryRdId(previousKey) === (rdId ?? null);
}

/** The selection slot of an Appointment list query key; the key's last entry. */
function appointmentQueryRdId(key: readonly unknown[]): string | null {
  const slot = key[key.length - 1];

  return typeof slot === "string" ? slot : null;
}

export function appointmentsListApiRequest(input: {
  showNonDefaultStatuses: boolean;
  cursor: string | null;
  timezone: string;
  rdId?: string;
}): { path: string; init: RequestInit } {
  const query = new URLSearchParams({ timezone: input.timezone });

  if (input.showNonDefaultStatuses) {
    query.set("showNonDefaultStatuses", "true");
  }

  if (input.cursor) query.set("cursor", input.cursor);

  if (input.rdId) query.set("rdId", input.rdId);

  return {
    path: `/api/appointments?${query.toString()}`,
    init: { method: "GET" },
  };
}

export function appointmentsPastWeekApiRequest(input: {
  week: string | null;
  showNonDefaultStatuses: boolean;
  timezone: string;
  rdId?: string;
}): { path: string; init: RequestInit } {
  const query = new URLSearchParams({ timezone: input.timezone, view: "past" });
  if (input.week) query.set("week", input.week);

  if (input.showNonDefaultStatuses) query.set("showNonDefaultStatuses", "true");

  if (input.rdId) query.set("rdId", input.rdId);

  return {
    path: `/api/appointments?${query.toString()}`,
    init: { method: "GET" },
  };
}

export function groupAppointments(
  appointments: AppointmentListItem[],
  input: { now: string; timezone: string }
): AppointmentDateGroup[] {
  const today = dateInTimezone(input.now, input.timezone);
  const tomorrow = nextCalendarDate(today);
  const sorted = [...appointments].sort(byStartAsc);
  const groups = new Map<string, AppointmentListItem[]>();

  for (const appointment of sorted) {
    const startDate = dateInTimezone(appointment.startsAt, input.timezone);
    // The server only returns current or future Appointments, so a start date
    // before Today means the Appointment is still in progress across midnight
    // — it belongs under Today, not in an invented past section.
    const date = startDate < today ? today : startDate;
    const group = groups.get(date);

    if (group) {
      group.push(appointment);
    } else {
      groups.set(date, [appointment]);
    }
  }

  return [...groups.entries()].map(([date, groupedAppointments]) => ({
    date,
    label: dateLabel({
      date,
      today,
      tomorrow,
      representativeStart: groupedAppointments[0]!.startsAt,
      timezone: input.timezone,
    }),
    appointments: groupedAppointments,
  }));
}

const DEFAULT_VISIBLE_STATUSES: ReadonlySet<AppointmentStatus> = new Set([
  "scheduled",
  "checked_in",
  "occurred",
  "unknown",
]);

export interface UpNextAppointment {
  appointment: AppointmentListItem;
  kind: "now" | "soon" | "next";
}

export function upNextAppointment(
  groups: AppointmentDateGroup[],
  input: { now: string; timezone: string }
): UpNextAppointment | null {
  const today = dateInTimezone(input.now, input.timezone);
  const nowMs = Date.parse(input.now);
  const appointment = groups
    .find((group) => group.date === today)
    ?.appointments.find(
      (item) => DEFAULT_VISIBLE_STATUSES.has(item.status) && !hasEnded(item, nowMs)
    );
  if (!appointment) return null;

  const startsInMs = Date.parse(appointment.startsAt) - Date.parse(input.now);
  const kind = startsInMs <= 0 ? "now" : startsInMs <= 60 * 60 * 1000 ? "soon" : "next";

  return { appointment, kind };
}

// Mirrors the server's lane split: an Appointment has ended once its end —
// or start + length, when the end is missing — is at or before now.
function hasEnded(
  item: Pick<AppointmentListItem, "startsAt" | "endsAt" | "lengthMinutes">,
  nowMs: number
): boolean {
  const startMs = Date.parse(item.startsAt);
  if (startMs >= nowMs) return false;

  const endMs = item.endsAt
    ? Date.parse(item.endsAt)
    : startMs + (item.lengthMinutes ?? 0) * 60_000;

  return endMs <= nowMs;
}

export interface EarlierAppointmentsSplit {
  endedToday: AppointmentListItem[];
  earlierThisWeek: AppointmentListItem[];
}

// The earlier lane carries every ended Appointment from the current week,
// including ones from earlier today. Those belong under Today with their
// status pill; the Earlier-this-week section starts at yesterday.
export function splitEarlierAppointments(
  earlier: AppointmentListItem[],
  input: { now: string; timezone: string }
): EarlierAppointmentsSplit {
  const today = dateInTimezone(input.now, input.timezone);
  const endedToday: AppointmentListItem[] = [];
  const earlierThisWeek: AppointmentListItem[] = [];

  for (const item of earlier) {
    const lane =
      dateInTimezone(item.startsAt, input.timezone) === today
        ? endedToday
        : earlierThisWeek;
    lane.push(item);
  }

  return { endedToday, earlierThisWeek };
}

export function appointmentsTodayCount(
  groups: AppointmentDateGroup[],
  input: { now: string; timezone: string; hasNextPage: boolean }
): number | null {
  const today = dateInTimezone(input.now, input.timezone);
  const count = groups.find((group) => group.date === today)?.appointments.length ?? 0;
  const pageReachesBeyondToday = groups.some((group) => group.date > today);

  return input.hasNextPage && !pageReachesBeyondToday ? null : count;
}

// RDs read their agenda in the clock they are living in: every Appointment
// time, day grouping, and greeting renders in the device timezone, not the
// Provider's (decided 2026-08-16 — "I see my agenda as I will live it").
// The list request sends the same zone so the server bounds the ended-today lane
// and Past weeks with the clock the client uses to group them.
export function deviceTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function timeOfDayGreeting(now: string, timezone: string): string {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      hour: "numeric",
    }).format(new Date(now))
  );
  if (hour < 12) return "Good morning";

  if (hour < 17) return "Good afternoon";

  return "Good evening";
}

export function headerDateLabel(now: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date(now));
}

export function shortDateLabel(now: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(now));
}

export function formatAppointmentTime(startsAt: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(startsAt));
}

export function formatAppointmentDate(startsAt: string, timezone: string): string {
  return formatDate(startsAt, timezone);
}

export function formatAppointmentTimeRange(
  item: Pick<AppointmentListItem, "startsAt" | "endsAt" | "lengthMinutes">,
  timezone: string
): string {
  const start = new Date(item.startsAt);
  const endMs = item.endsAt
    ? Date.parse(item.endsAt)
    : item.lengthMinutes
      ? start.getTime() + item.lengthMinutes * 60_000
      : Number.NaN;
  const format = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  });
  if (!Number.isFinite(endMs) || endMs <= start.getTime()) return format.format(start);

  return format.formatRange(start, new Date(endMs));
}

export function compactDayTime(startsAt: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  }).formatToParts(new Date(startsAt));
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${read("weekday")} ${read("hour")}:${read("minute")}`;
}

export function scheduleRowLabel(
  startsAt: string,
  input: { now: string; timezone: string }
): string {
  // Within the coming week keep the design's weekday form ("Mon 9:30"); beyond
  // it weekday names would repeat, so a bare date ("Aug 25") disambiguates.
  const withinWeek =
    Date.parse(startsAt) - Date.parse(input.now) < 6 * 24 * 60 * 60 * 1000;
  if (withinWeek) return compactDayTime(startsAt, input.timezone);

  return new Intl.DateTimeFormat("en-US", {
    timeZone: input.timezone,
    month: "short",
    day: "numeric",
  }).format(new Date(startsAt));
}

function readBatchCount(value: unknown): number | undefined {
  const parsed =
    typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value;
  if (typeof parsed !== "number" || !Number.isSafeInteger(parsed)) return undefined;

  const isReplayDepth = parsed >= 2 && parsed <= MAX_RESTORED_APPOINTMENT_BATCHES;

  return isReplayDepth ? parsed : undefined;
}

function dateLabel(input: {
  date: string;
  today: string;
  tomorrow: string;
  representativeStart: string;
  timezone: string;
}): string {
  if (input.date === input.today) return "Today";

  if (input.date === input.tomorrow) return "Tomorrow";

  return formatDate(input.representativeStart, input.timezone);
}

function nextCalendarDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  const next = new Date(Date.UTC(year!, month! - 1, day! + 1));

  return [
    next.getUTCFullYear(),
    String(next.getUTCMonth() + 1).padStart(2, "0"),
    String(next.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function formatDate(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function dateInTimezone(value: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${read("year")}-${read("month")}-${read("day")}`;
}

function byStartAsc(a: AppointmentListItem, b: AppointmentListItem): number {
  return Date.parse(a.startsAt) - Date.parse(b.startsAt);
}
